// Verwalten › Meine Daten (h-umzug): Datenauskunft und Datenmitnahme für das
// eigene Radio (Art. 15 und 20 DSGVO).
//
// Ein Knopf stößt den Export an, die Liste zeigt ihn mit Download. Was drin
// ist und was nicht, steht daneben – ehrlich und vollständig. Grenze: einer
// gleichzeitig, einer je Tag; nach 7 Tagen verschwindet das Archiv.

import { useState, type ReactNode } from "react";
import { umzugApi } from "../api";
import { errorText } from "../components/streaming-ui";
import { Meldungen, Sym } from "../components/social-ui";
import { Button, Callout, Card, Page, PageHeader } from "../components/ui";
import { ExportListe, laeuftNoch, useAuftraege } from "../components/umzug-ui";

export function MeineDatenPage(): JSX.Element {
  const [mitschnitte, setMitschnitte] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const { liste, neu, fehler: ladeFehler } = useAuftraege(() => umzugApi.datenexporte().then((r) => r.exporte));
  const aktiv = !!liste?.some(laeuftNoch);

  const starten = async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      await umzugApi.datenexportStarten(mitschnitte);
      setHinweis("Der Export läuft. Das dauert je nach Mediathek einige Minuten – die Liste aktualisiert sich von selbst.");
      neu();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Page className="max-w-4xl">
      <PageHeader
        eyebrow="Verwalten"
        title="Meine Daten"
        description="Alle Daten eures Radios als Archiv – zum Nachsehen, Aufbewahren oder Mitnehmen (Auskunft und Datenübertragbarkeit nach DSGVO)."
      />
      <Meldungen error={fehler ?? (ladeFehler ? errorText(new Error(ladeFehler)) : null)} notice={hinweis} onClear={() => setHinweis(null)} />

      <Card title="Datenexport anstoßen" className="mb-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-start gap-2.5 text-sm">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={mitschnitte} onChange={(e) => setMitschnitte(e.target.checked)} />
            <span>
              <b className="font-medium text-primary">Mitschnitte einschließen</b>
              <span className="block text-xs text-muted">Sendungsmitschnitte können viele Gigabyte groß sein.</span>
            </span>
          </label>
          <Button variant="primary" className="self-start sm:self-auto" disabled={laeuft || aktiv} onClick={() => void starten()}>
            <Sym name="download" /> {aktiv ? "Export läuft …" : "Datenexport anstoßen"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted">Einer gleichzeitig und einer je Tag. Fertige Archive liegen 7 Tage zum Herunterladen bereit.</p>
        <div className="mt-5">
          <div className="section-label mb-2">Eure Exporte</div>
          {liste === null ? (
            <p className="text-sm text-muted">Wird geladen …</p>
          ) : (
            <ExportListe
              exporte={liste}
              leer="Noch kein Datenexport."
              onDownload={(a) => { umzugApi.datenexportHerunterladen(a.id).catch((err) => setFehler(errorText(err))); }}
            />
          )}
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Was drin ist">
          <ul className="space-y-2 text-sm text-secondary">
            <Punkt symbol="check">Alle Datensätze des Radios – Team, Kanäle, Sendepläne, Stundenuhren, Mediathek-Einträge, Seiten, Beiträge, Hörer-Nachrichten, Statistiken, Protokoll – als JSON-Zeilen, eine Datei je Tabelle.</Punkt>
            <Punkt symbol="check">Die Dateien: Mediathek, Bilder und Cover der Hörerseite, Sprachnachrichten; Mitschnitte nach Wahl.</Punkt>
            <Punkt symbol="check">Ein Manifest mit Stand der Suite, Zählungen und einer Prüfsumme (SHA-256) für jede Datei.</Punkt>
          </ul>
        </Card>
        <Card title="Was nicht drin ist">
          <ul className="space-y-2 text-sm text-secondary">
            <Punkt symbol="shield">Geheimnisse: Passwort-Hashes, Zwei-Faktor-Schlüssel und Wiederherstellungscodes, Sende- und Server-Passwörter, Zugangsdaten von Meldezielen (z. B. Telegram-Bot), Einmal-Links.</Punkt>
            <Punkt symbol="shield">Interne Notizen des Betreibers und IP-Adressen im Protokoll.</Punkt>
            <Punkt symbol="shield">Anmeldesitzungen und laufende Hintergrund-Aufträge.</Punkt>
          </ul>
        </Card>
      </div>
      <Callout tone="info" className="mt-5">
        Das Archiv ist ein gewöhnliches <code className="font-mono">.tar.gz</code> und lässt sich mit jedem Entpacker öffnen. Für einen Umzug zu einem
        anderen Anbieter mit der webradiosuite fragt bitte den Betreiber nach einem Umzugsarchiv – dieses hier enthält bewusst keine Zugangsdaten.
      </Callout>
    </Page>
  );
}

function Punkt({ symbol, children }: { symbol: string; children: ReactNode }): JSX.Element {
  return (
    <li className="flex gap-2">
      <Sym name={symbol} className={`mt-0.5 h-4 w-4 flex-shrink-0 ${symbol === "check" ? "text-emerald-500" : "text-muted"}`} />
      <span>{children}</span>
    </li>
  );
}
